// Slider (§10): double-click resets, Alt-drag gives fine steps, number field beside it, arrow keys nudge
// when focused (Shift = ×10). Reports live changes and a final value on release (one undo step).
import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useT, type MessageKey } from '../../i18n';
import s from './Slider.module.css';

export type SliderProps = {
  label: MessageKey;
  value: number;
  min: number;
  max: number;
  step: number;
  defaultValue?: number;
  /** Decimal places shown in the number field. */
  digits?: number;
  onChange: (v: number, final: boolean) => void;
  testId?: string;
};

const clampTo = (v: number, min: number, max: number, step: number) => {
  const c = Math.min(max, Math.max(min, v));
  return Math.round(c / step) * step;
};

export function Slider({
  label,
  value,
  min,
  max,
  step,
  defaultValue = 0,
  digits = 0,
  onChange,
  testId,
}: SliderProps) {
  const t = useT();
  const track = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startV: number; fine: boolean } | null>(null);
  const [text, setText] = useState<string | null>(null);
  const span = max - min;
  const pct = ((value - min) / span) * 100;
  const zeroPct = ((Math.min(max, Math.max(min, 0)) - min) / span) * 100;

  const valueAt = (clientX: number) => {
    const r = track.current!.getBoundingClientRect();
    return min + ((clientX - r.left) / r.width) * span;
  };
  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    const fine = e.altKey;
    const start = fine ? value : clampTo(valueAt(e.clientX), min, max, step);
    drag.current = { startX: e.clientX, startV: start, fine };
    if (!fine) onChange(start, false);
  };
  const onPointerMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const r = track.current!.getBoundingClientRect();
    const k = e.altKey ? 0.1 : 1;
    const v =
      d.fine || e.altKey ? d.startV + ((e.clientX - d.startX) / r.width) * span * k : valueAt(e.clientX);
    onChange(clampTo(v, min, max, step), false);
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    onChange(value, true);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const k = e.shiftKey ? 10 : 1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp')
      onChange(clampTo(value + step * k, min, max, step), true);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
      onChange(clampTo(value - step * k, min, max, step), true);
    else if (e.key === 'Home') onChange(min, true);
    else if (e.key === 'End') onChange(max, true);
    else return;
    e.preventDefault();
    e.stopPropagation();
  };
  const commitText = () => {
    if (text === null) return;
    const v = Number(text.replace(',', '.'));
    if (Number.isFinite(v)) onChange(clampTo(v, min, max, step), true);
    setText(null);
  };
  const name = t(label);
  return (
    <div className={s.row} data-testid={testId}>
      <label
        className={s.label}
        onDoubleClick={() => onChange(defaultValue, true)}
        title={t('doubleClickReset')}
      >
        {name}
      </label>
      <div
        ref={track}
        className={s.track}
        role="slider"
        tabIndex={0}
        aria-label={name}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        title={t('doubleClickReset')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => onChange(defaultValue, true)}
        onKeyDown={onKeyDown}
      >
        <div
          className={s.fill}
          style={{ left: `${Math.min(zeroPct, pct)}%`, width: `${Math.abs(pct - zeroPct)}%` }}
        />
        <div className={s.thumb} style={{ left: `${pct}%` }} />
      </div>
      <input
        className={s.num}
        aria-label={name}
        value={text ?? value.toFixed(digits)}
        onChange={(e) => setText(e.target.value)}
        onBlur={commitText}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitText();
          if (e.key === 'Escape') setText(null);
          e.stopPropagation();
        }}
      />
    </div>
  );
}
